import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Wand2, Lock, Unlock, Clock, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { PlanningJourRow } from './planning/PlanningJourRow';
import { SyntheseModules } from './planning/SyntheseModules';
import { AffectationGroupee } from './planning/AffectationGroupee';
import { ExceptionsJoursSemaine } from './planning/ExceptionsJoursSemaine';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Users, MapPin, BookOpen } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOURS_PER_DAY = 7;

// Planning pédagogique en fil chronologique : une ligne par journée (formateur, lieu, modules + durées).
const PlanningPedagogiqueTab = ({ planning, formations, formationId, modules, formateurs, lieux, onPlanningChange, dossierId, token, referentId, lieuPrincipal, canUnlock = false, deverrouilles = [], onDeverrouillesChange }) => {
  const [verrous, setVerrous] = useState({ jours: {}, modules_aft: {}, modules_evalues: {} });
  const toggleUnlock = (date) => onDeverrouillesChange?.(deverrouilles.includes(date) ? deverrouilles.filter(d => d !== date) : [...deverrouilles, date]);
  // Modules non supprimables sur une journee : evaluation realisee ce jour-la (ou a une date inconnue)
  const modulesEvaluesLe = (date) => new Set(Object.entries(verrous.modules_evalues || {}).filter(([, dates]) => dates.includes('*') || dates.includes(date)).map(([mid]) => mid));
  const [creneaux, setCreneaux] = useState([]);
  const [vue, setVue] = useState('formateur');

  useEffect(() => {
    axios.get(`${API_URL}/creneaux`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setCreneaux(r.data || [])).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!dossierId) return;
    axios.get(`${API_URL}/dossiers/${dossierId}/planning-verrous`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVerrous(r.data || { jours: {}, modules_aft: {} })).catch(() => {});
  }, [dossierId, token]);

  const formation = formations.find(f => f.id === formationId);
  const modulesFormation = useMemo(() => formation?.modules_formation || [], [formation]);
  const sortedPlanning = useMemo(() => [...planning].sort((a, b) => a.date.localeCompare(b.date)), [planning]);
  const formationModuleIds = new Set(modulesFormation.map(mf => mf.module_id));
  const formationModulesList = modules.filter(m => formationModuleIds.has(m.id));
  const otherModulesList = modules.filter(m => !formationModuleIds.has(m.id));
  const nbVerrous = Object.keys(verrous.jours || {}).length;
  const aftList = Object.values(verrous.modules_aft || {});

  const updateDay = (newDay) => onPlanningChange(planning.map(p => p.date === newDay.date ? newDay : p));

  // Applique le déroulé de la formation sur les jours non verrouillés, en conservant les modules verrouillés (jours confirmés / AFT).
  const autoFillModules = () => {
    const queue = modulesFormation.map(mf => {
      const mod = modules.find(m => m.id === mf.module_id);
      return { module_id: mf.module_id, remaining: mf.duree_heures || mod?.duree_heures || HOURS_PER_DAY };
    }).filter(q => !verrous.modules_aft?.[q.module_id]);
    if (queue.length === 0 && modulesFormation.length === 0) return;

    const newPlanning = sortedPlanning.map(p => ({ ...p }));
    // Heures déjà fixées par les jours verrouillés → déduites du déroulé
    for (const p of newPlanning) {
      if (!verrous.jours?.[p.date] || deverrouilles.includes(p.date)) continue;
      for (const mp of p.modules_pedagogiques || []) {
        const q = queue.find(x => x.module_id === mp.module_id);
        if (q) q.remaining -= (parseFloat(mp.duree_heures) || 0);
      }
    }
    let queueIdx = 0;
    let filledDays = 0;
    for (const p of newPlanning) {
      if (p.type_journee === 'stage' || (verrous.jours?.[p.date] && !deverrouilles.includes(p.date))) continue;
      const evalues = modulesEvaluesLe(p.date);
      const kept = (p.modules_pedagogiques || []).filter(mp => verrous.modules_aft?.[mp.module_id] || evalues.has(mp.module_id));
      let hoursLeft = HOURS_PER_DAY - kept.reduce((s, mp) => s + (parseFloat(mp.duree_heures) || 0), 0);
      const dayModules = [...kept];
      while (queueIdx < queue.length && queue[queueIdx].remaining <= 0) queueIdx++;
      while (hoursLeft > 0 && queueIdx < queue.length) {
        const item = queue[queueIdx];
        const take = Math.min(item.remaining, hoursLeft);
        dayModules.push({ module_id: item.module_id, duree_heures: take, obligatoire: false });
        item.remaining -= take;
        hoursLeft -= take;
        if (item.remaining <= 0) queueIdx++;
      }
      if (dayModules.length > kept.length) filledDays++;
      p.modules_pedagogiques = dayModules;
    }
    onPlanningChange(newPlanning);

    const heuresRestantes = queue.reduce((s, q) => s + Math.max(0, q.remaining), 0);
    const joursVides = newPlanning.filter(p => p.type_journee !== 'stage' && !(p.modules_pedagogiques || []).length).length;
    if (heuresRestantes > 0) toast.warning(`Déroulé appliqué sur ${filledDays} jour(s), mais il manque ${heuresRestantes}h de planning pour tout placer`);
    else if (joursVides > 0) toast.success(`Déroulé appliqué sur ${filledDays} jour(s) — ${joursVides} jour(s) de formation restent libres`);
    else toast.success(`Déroulé de la formation appliqué sur ${filledDays} jour(s)`);
  };

  const synthesis = useMemo(() => {
    const expected = {};
    modulesFormation.forEach(mf => {
      const mod = modules.find(m => m.id === mf.module_id);
      expected[mf.module_id] = { module_id: mf.module_id, code: mod?.code || '?', titre: mod?.titre || '?', duree_prevue: mf.duree_heures || mod?.duree_heures || 0, duree_planifiee: 0, jours: 0, inFormation: true };
    });
    sortedPlanning.forEach(day => {
      (day.modules_pedagogiques || []).forEach(mp => {
        if (!mp?.module_id) return;
        if (!expected[mp.module_id]) {
          const mod = modules.find(m => m.id === mp.module_id);
          expected[mp.module_id] = { module_id: mp.module_id, code: mod?.code || '?', titre: mod?.titre || '?', duree_prevue: 0, duree_planifiee: 0, jours: 0, inFormation: false };
        }
        expected[mp.module_id].duree_planifiee += (parseFloat(mp.duree_heures) || 0);
        expected[mp.module_id].jours += 1;
      });
    });
    return Object.values(expected);
  }, [modulesFormation, modules, sortedPlanning]);

  return (
    <Card className="border-0 shadow-sm" data-testid="planning-pedagogique">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500" /> Planning pédagogique</h3>
            <p className="text-sm text-slate-500">Pour chaque journée, choisissez le formateur principal (et son créneau), les intervenants supplémentaires, le lieu et les modules réalisés avec leur durée.</p>
          </div>
          {modulesFormation.length > 0 && (
            <button type="button" onClick={autoFillModules} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors" data-testid="btn-auto-fill-modules">
              <Wand2 className="w-3.5 h-3.5" /> Appliquer le déroulé de la formation
            </button>
          )}
        </div>

        <Tabs value={vue} onValueChange={setVue}>
          <TabsList className="grid grid-cols-3 w-full max-w-lg" data-testid="planning-vue-tabs">
            <TabsTrigger value="formateur" data-testid="planning-vue-formateur"><Users className="w-3.5 h-3.5 mr-1.5" /> Formateurs</TabsTrigger>
            <TabsTrigger value="lieu" data-testid="planning-vue-lieu"><MapPin className="w-3.5 h-3.5 mr-1.5" /> Lieux</TabsTrigger>
            <TabsTrigger value="module" data-testid="planning-vue-module"><BookOpen className="w-3.5 h-3.5 mr-1.5" /> Modules</TabsTrigger>
          </TabsList>
        </Tabs>

        {sortedPlanning.length > 0 && vue !== 'module' && (
          <AffectationGroupee key={`${referentId}-${lieuPrincipal}`} planning={planning} formateurs={formateurs} lieux={lieux} referentId={referentId} lieuPrincipal={lieuPrincipal} onPlanningChange={onPlanningChange} vue={vue} />
        )}
        {sortedPlanning.length > 0 && vue === 'formateur' && (
          <ExceptionsJoursSemaine planning={planning} formateurs={formateurs} creneaux={creneaux} referentId={referentId} onPlanningChange={onPlanningChange} />
        )}

        {(nbVerrous > 0 || aftList.length > 0) && (
          <div className="flex flex-wrap gap-3 text-[11px] text-slate-600 bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2" data-testid="planning-verrous-info">
            {nbVerrous > 0 && <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-amber-600" /> {nbVerrous} journée(s) verrouillée(s) : modules confirmés ou présences validées par le formateur.{canUnlock ? ' En tant qu\'administrateur, vous pouvez déverrouiller une journée pour la modifier (les modules déjà évalués restent non supprimables).' : ''}</span>}
            {deverrouilles.length > 0 && <span className="flex items-center gap-1 text-indigo-700 font-medium" data-testid="planning-deverrouilles-info"><Unlock className="w-3 h-3" /> {deverrouilles.length} journée(s) déverrouillée(s) pour cette modification — pensez à enregistrer.</span>}
            {aftList.map(a => <span key={a.aft_id} className="flex items-center gap-1"><GraduationCap className="w-3 h-3 text-cyan-600" /> Module certifiant lié à l'AFT <span className="font-semibold">{a.aft_code}</span> : ses journées se synchronisent automatiquement sur l'AFT à l'enregistrement.</span>)}
          </div>
        )}

        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 text-slate-500 uppercase tracking-wide text-[10px]">
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-2 py-2 text-left font-medium">Type</th>
                {vue === 'formateur' && <th className="px-2 py-2 text-left font-medium">Formateur principal · créneau · intervenants</th>}
                {vue === 'lieu' && <th className="px-2 py-2 text-left font-medium">Lieu</th>}
                {vue === 'module' && <th className="px-2 py-2 text-left font-medium">Modules · durée</th>}
                {vue !== 'formateur' && <th className="px-2 py-2 text-left font-medium">Formateur</th>}
                {vue === 'module' && <th className="px-2 py-2 text-center font-medium">Total</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedPlanning.map(day => (
                <PlanningJourRow key={day.date} vue={vue} day={day} formateurs={formateurs} lieux={lieux} allModules={modules} formationModulesList={formationModulesList} otherModulesList={otherModulesList}
                  modulesAft={verrous.modules_aft || {}} verrou={verrous.jours?.[day.date]} deverrouille={deverrouilles.includes(day.date)} canUnlock={canUnlock} onToggleUnlock={toggleUnlock} modulesEvalues={modulesEvaluesLe(day.date)}
                  referentId={referentId} lieuPrincipal={lieuPrincipal} creneaux={creneaux} onChange={updateDay} />
              ))}
            </tbody>
          </table>
        </div>

        {synthesis.length > 0 && <SyntheseModules synthesis={synthesis} sortedPlanning={sortedPlanning} dossierId={dossierId} token={token} />}
      </CardContent>
    </Card>
  );
};

export { PlanningPedagogiqueTab };
